<template>
  <div class="indus-code-selection">
    <el-cascader
      v-model="selectedValues"
      :options="cascaderOptions"
      :props="finalCascaderProps"
      :filter-method="indusCodeFilterMethod"
      :placeholder="placeholder"
      :clearable="clearable"
      :filterable="filterable"
      :collapse-tags="collapseTags"
      :multiple="multiple"
      :size="size"
      :disabled="disabled || loading"
      :show-all-levels="false"
      @change="handleChange"
      @expand-change="handleExpandChange"
      @blur="handleBlur"
      @focus="handleFocus"
      @visible-change="handleVisibleChange"
      @remove-tag="handleRemoveTag"
    >
      <template #default="{ data }">
        <span>{{ data.label }}</span>
        <span v-if="showCode" class="code-text">({{ data.value }})</span>
      </template>
    </el-cascader>
  </div>
</template>

<script>
export default {
  name: 'IndusCodeSelection',
  props: {
    // v-model 值
    value: {
      type: [Array, String, Number],
      default: () => []
    },
    // 是否多選
    multiple: {
      type: Boolean,
      default: false
    },
    // 佔位符文字
    placeholder: {
      type: String,
      default: '請選擇行業代號'
    },
    // 是否可清空
    clearable: {
      type: Boolean,
      default: true
    },
    // 是否可搜尋
    filterable: {
      type: Boolean,
      default: true
    },
    // 多選時是否折疊標籤
    collapseTags: {
      type: Boolean,
      default: true
    },
    // 尺寸
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['large', 'default', 'small'].includes(value)
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否顯示代碼
    showCode: {
      type: Boolean,
      default: true
    },
    // 自定義級別設定
    customProps: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'input',
    'change',
    'expand-change',
    'blur',
    'focus',
    'visible-change',
    'remove-tag'
  ],
  data() {
    return {
      cascaderOptions: [],
      loading: false,
      selectedValues: this.value
    }
  },
  computed: {
    // 您原本的 cascaderProps
    cascaderProps() {
      return {
        value: 'value',
        label: 'label',
        children: 'children',
        multiple: this.multiple,
        checkStrictly: false,
        emitPath: true,
        separator: ' / ',
        lazy: false,
        lazyLoad: null,
        ...this.customProps
      }
    },
    
    // 💡 新增：自動為行動端/iPad 切換展開觸發器的 props
    finalCascaderProps() {
      const isMobileOrTablet = typeof window !== 'undefined' && 
        (/iPad|iPhone|iPod|android|Macintosh/i.test(navigator.userAgent) && ('ontouchend' in document));
      
      return {
        ...this.cascaderProps,
        // PC 版用 click，行動版/iPad 自動切換成 hover
        expandTrigger: isMobileOrTablet ? 'hover' : 'click'
      };
    }
  },  

  watch: {
    value: {
      handler(newVal) {
        this.selectedValues = newVal
      },
      immediate: true,
      deep: true
    },
    selectedValues: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  async mounted() {
    await this.loadIndusCodeData()
  },
  methods: {

 

    // 載入產業代碼數據
    async loadIndusCodeData() {
      this.loading = true
      try {
        const response = await fetch('/indusCode.json')
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        
        // 檢查數據結構
        if (data && data.data && Array.isArray(data.data)) {
          this.cascaderOptions = data.data
          //console.log('成功載入產業代碼數據:', data.meta?.total_records || 0, '筆記錄')
        } else {
          throw new Error('產業代碼數據格式不正確')
        }
      } catch (error) {
        console.error('載入產業代碼數據失敗:', error)
        this.$message?.error?.('載入產業代碼數據失敗')
        this.cascaderOptions = []
      } finally {
        this.loading = false
      }
    },

    // 選中值發生變化時的處理
    handleChange(value) {
      this.selectedValues = value
      
      // 獲取選中項目的詳細信息
      const selectedItems = this.getSelectedItems(value)
      
      // 只發送一次事件，同時傳遞 value 和 selectedItems
      this.$emit('change', value, selectedItems)
    },

    // 獲取選中項目的詳細信息
    getSelectedItems(values) {
      if (!values || values.length === 0) return []
      
      if (this.multiple) {
        return values.map(path => this.getItemByPath(path))
      } else {
        return [this.getItemByPath(values)]
      }
    },

    // 根據路徑獲取項目信息
    getItemByPath(path) {
      if (!path || path.length === 0) return null
      
      let current = this.cascaderOptions
      let item = null
      
      for (let i = 0; i < path.length; i++) {
        const value = path[i]
        item = current.find(option => option.value === value)
        if (!item) break
        if (i < path.length - 1) {
          current = item.children || []
        }
      }
      
      return item ? {
        value: item.value,
        label: item.label,
        level: item.level,
        fullPath: path,
        fullLabel: this.getFullLabel(path)
      } : null
    },

    // 獲取完整標籤路徑
    getFullLabel(path) {
      const labels = []
      let current = this.cascaderOptions
      
      for (let i = 0; i < path.length; i++) {
        const value = path[i]
        const item = current.find(option => option.value === value)
        if (item) {
          labels.push(item.label)
          current = item.children || []
        }
      }
      
      return labels.join(' / ')
    },

    // 展開節點發生變化時的處理
    handleExpandChange(values) {
      this.$emit('expand-change', values)
    },

    // 失去焦點時的處理
    handleBlur(event) {
      this.$emit('blur', event)
    },

    // 獲得焦點時的處理
    handleFocus(event) {
      this.$emit('focus', event)
    },

    
    // 下拉框出現/隱藏時的處理（將 iPad 觸控修復融入原本的 method）
    handleVisibleChange(visible) {
      this.$emit('visible-change', visible)

      const isMobileOrTablet = typeof window !== 'undefined' && 
        (/iPad|iPhone|iPod|android|Macintosh/i.test(navigator.userAgent) && ('ontouchend' in document));

      if (visible && isMobileOrTablet) {
        this.$nextTick(() => {
          const nodes = document.querySelectorAll('.el-cascader-node');
          
          nodes.forEach(node => {
            // 判斷是否為最後一層（葉子節點）
            const isLeaf = node.classList.contains('is-selectable') || !node.querySelector('.el-icon-arrow-right');
            
            if (isLeaf && !node.classList.contains('has-touch-bound')) {
              node.classList.add('has-touch-bound');
              
              node.addEventListener('touchend', (e) => {
                // 阻止 iOS 原生 click 判定延遲與雙擊
                e.preventDefault(); 
                e.stopPropagation();
                
                // 核心：如果是多選，點擊內部的 checkbox，若是單選，直接點擊節點
                const checkbox = node.querySelector('.el-checkbox__original');
                if (checkbox) {
                  // 多選模式下：強行觸發原生的 checkbox 點擊，使打勾生效
                  checkbox.click();
                } else {
                  // 單選模式下：直接觸發節點點擊
                  node.click();
                }
              }, { passive: false });
            }
          });
        });
      }
    },

    // 多選模式下移除tag時的處理
    handleRemoveTag(value) {
      this.$emit('remove-tag', value)
    },

    // 自定義搜尋：優先用 value 欄位比對，四位代碼時採精準比對
    indusCodeFilterMethod(node, keyword) {
      const input = String(keyword || '').trim().toLowerCase()
      if (!input) return true

      const code = String(node.value || '').trim().toLowerCase()
      if (!code) return false

      if (/^\d{4}$/.test(input)) {
        return code === input
      }

      return code.includes(input)
    },

    // 清空選擇
    clearSelection() {
      this.selectedValues = this.multiple ? [] : ''
    },

    // 獲取當前選中的完整信息
    getSelectedInfo() {
      return this.getSelectedItems(this.selectedValues)
    },

    // 設置選中值
    setSelectedValues(values) {
      this.selectedValues = values
    }
  }
}
</script>

<style lang="scss" scoped>
.indus-code-selection {
  .code-text {
    color: #909399;
    font-size: 12px;
    margin-left: 8px;
  }
}

// 自定義 cascader 樣式
:deep(.el-cascader) {
  width: 100%;
  
  .el-cascader__tags {
    max-height: 100px;
    overflow-y: auto;
  }
}


// 自定義 cascader 樣式
:deep(.el-cascader) {
  width: 100%;
  
  .el-cascader__tags {
    max-height: 100px;
    overflow-y: auto;
  }
}

// 💡 補上下方的行動端觸控優化 CSS (使用 :deep() 穿透 Scoped)
:deep(.el-cascader-node),
:deep(.el-cascader-menu__list) {
  touch-action: manipulation !important;
  -webkit-tap-highlight-color: transparent;
}

:deep(.el-cascader-node.is-selectable) {
  touch-action: manipulation !important;
}

:deep(.el-cascader-node__label) {
  pointer-events: auto !important;
  width: 100%;
  height: 100%;
  display: block;
}

// 如果有複選框，確保複選框區域也能順暢穿透點擊
:deep(.el-checkbox) {
  pointer-events: auto !important;
}

</style>